import { createTheme, Theme } from '@mui/material/styles';
import { enUS, viVN } from '@mui/material/locale';
import { ColorMode, colorTokens } from './appColorTokens';
import { formControlOverrides } from './formControlOverrides';

const FONT_SANS = "'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";

// Builds the MUI theme from the design tokens so stock MUI components
// (dialogs, inputs, menus, snackbars) match the hand-styled surfaces.
// The MUI locale translates built-in component text (pagination, aria labels).
export const createAppTheme = (mode: ColorMode, language = 'en'): Theme => {
  const t = colorTokens[mode];

  return createTheme({
    palette: {
      mode,
      primary: { main: t.primary, contrastText: '#ffffff' },
      secondary: { main: t.primary2 },
      success: { main: t.success },
      warning: { main: t.warn },
      error: { main: t.danger },
      background: { default: t.surface, paper: t.card },
      text: { primary: t.fg, secondary: t.muted },
      divider: t.border,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: FONT_SANS,
      button: { textTransform: 'none', fontWeight: 500 },
      h1: { fontWeight: 600, letterSpacing: '-0.03em' },
      h2: { fontWeight: 600, letterSpacing: '-0.025em' },
      h3: { fontWeight: 600, letterSpacing: '-0.025em' },
      h4: { fontWeight: 600, letterSpacing: '-0.025em', fontSize: '1.625rem' },
      h5: { fontWeight: 600, letterSpacing: '-0.02em', fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '0.9375rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 500, color: t.muted },
      body2: { fontSize: '0.875rem' },
      caption: { fontSize: '0.75rem', color: t.muted },
    },
    components: {
      ...formControlOverrides(t),
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.surface, color: t.fg },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: t.border },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { borderRadius: 12, borderColor: t.border, backgroundColor: t.card } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 14, border: `1px solid ${t.border}`, boxShadow: '0 20px 50px rgba(0,0,0,.25)' },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em' } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
          outlined: { borderColor: t.border },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 500, minHeight: 40 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.border, fontVariantNumeric: 'tabular-nums' },
          head: { color: t.muted, fontSize: '0.75rem', fontWeight: 500 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 99, backgroundColor: t.mutedBg, height: 8 },
          bar: { borderRadius: 99 },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { fontSize: '0.75rem' } },
      },
    },
  }, language === 'vi' ? viVN : enUS);
};
